import { UserService } from 'src/app/store/settings/user/service/user.service';
import { forkJoin, map, Observable, take } from 'rxjs';
import { IndexedDBService } from '../services/indexedDb.service';
import { inject } from '@angular/core';
import { ExpenseTrackerStoreState } from '../store/reducers';
import { Store } from 'src/app/common/state/store';
import {
  iTrack,
  iIncome,
  iGroup,
  IBudgetDetail,
  iReoccurringTracks,
  IGroupTags,
  iIgnoreTags,
  IIconUpload,
} from '../common/interface';
import { GetBudgetDetailSuccessAction } from '../store/actions/budget-detail.action';
import { LoadExpensesAction } from '../store/actions/expenses.action';
import { GetGroupTagsSuccessAction } from '../store/actions/group-tags.action';
import { LoadGroupsAction } from '../store/actions/group.action';
import { LoadIconUploadAction } from '../store/actions/icon-upload.action';
import { LoadIgnoreTagsAction } from '../store/actions/ignore-tags.action';
import { LoadIncomeAction } from '../store/actions/income.action';
import { LoadReoccurringTracksAction } from '../store/actions/reoccurring-tracks.action';
import {
  Expense,
  ExpenseEngineService,
} from '../services/expense-engine.service';

export function LoadExpenseTrackerData(): Observable<any> {
  const indexedDBService: IndexedDBService = inject(IndexedDBService);
  const store = inject(Store<ExpenseTrackerStoreState>);
  const userService: UserService = inject(UserService);
  const expenseEngineService: ExpenseEngineService =
    inject(ExpenseEngineService);
  const currentYear = new Date().getFullYear();
  const expenseUserIds = userService.getTrackerPermissionUserByTrackerTypeId(1);

  indexedDBService.userId = userService.userId;
  indexedDBService.trackerPermissions = userService.trackerPermissions();

  return forkJoin([
    indexedDBService.getAllFromDbForTableByUser<iTrack>(
      'tracker',
      expenseUserIds,
      currentYear,
    ),
    indexedDBService.getAllFromDbForTableByUser<iIncome>(
      'income',
      userService.getTrackerPermissionUserByTrackerTypeId(2),
    ),
    indexedDBService.getAllFromDbForTableByUser<iGroup>(
      'groups',
      userService.getTrackerPermissionUserByTrackerTypeId(null),
    ),
    indexedDBService.getAllFromDbForTableByUser<IBudgetDetail>(
      'budget_detail',
      userService.getTrackerPermissionUserByTrackerTypeId(null),
    ),
    indexedDBService.getAllFromDbForTableByUser<iReoccurringTracks>(
      'reoccurring_tracks',
      userService.getTrackerPermissionUserByTrackerTypeId(null),
    ),
    indexedDBService.getAllFromDbForTableByUser<IGroupTags>(
      'group_tags',
      userService.getTrackerPermissionUserByTrackerTypeId(null),
    ),
    indexedDBService.getAllFromDbForTableByUser<iIgnoreTags>(
      'ignore_tags',
      userService.getTrackerPermissionUserByTrackerTypeId(null),
    ),
    indexedDBService.getAllFromDbForTableByUser<IIconUpload>(
      'icon_uploads',
      userService.getTrackerPermissionUserByTrackerTypeId(null),
    ),
  ]).pipe(
    take(1),
    map(
      ([
        tracks,
        income,
        groups,
        budgetDetails,
        reoccurringTracks,
        groupTags,
        ignoreTags,
        icons,
      ]) => {
        if (tracks && tracks.length > 0) {
          store.dispatch(LoadExpensesAction({ tracks: tracks as iTrack[] }));
        }
        if (income && income.length > 0) {
          store.dispatch(LoadIncomeAction({ income: income as iIncome[] }));
        }
        if (groups && groups.length > 0) {
          store.dispatch(LoadGroupsAction({ groups: groups as iGroup[] }));
        }
        if (budgetDetails && budgetDetails.length > 0) {
          store.dispatch(GetBudgetDetailSuccessAction({ budgetDetails }));
        }
        if (reoccurringTracks && reoccurringTracks.length > 0) {
          store.dispatch(LoadReoccurringTracksAction({ reoccurringTracks }));
        }
        if (groupTags && groupTags.length > 0) {
          store.dispatch(
            GetGroupTagsSuccessAction({
              groupTags: groupTags as IGroupTags[],
            }),
          );
        }
        if (ignoreTags && ignoreTags.length > 0) {
          store.dispatch(
            LoadIgnoreTagsAction({ tags: ignoreTags as iIgnoreTags[] }),
          );
        }
        if (icons && icons.length > 0) {
          store.dispatch(
            LoadIconUploadAction({ icons: icons as IIconUpload[] }),
          );
        }
        // if there are no tracks from indexedDb,
        // expenseEngine.setExpenses will not
        // generate summary, hence no expenseSummaryDone$
        // callback occurs. We simply redirect to expense home screen
        if (tracks && tracks.length > 0) {
          expenseEngineService.setExpenses(
            (structuredClone(tracks) || []) as Expense[],
          );
        }
      },
    ),
  );
}
